import {
    BLEND_NORMAL,
    CULLFACE_NONE,
    FILTER_LINEAR,
    FILTER_LINEAR_MIPMAP_LINEAR,
    ADDRESS_CLAMP_TO_EDGE,
    BoundingBox,
    BoxGeometry,
    Color,
    Entity,
    Mat4,
    Mesh,
    MeshInstance,
    Quat,
    SphereGeometry,
    StandardMaterial,
    Texture,
    Vec3
} from 'playcanvas';

import { Element, ElementType } from './element';
import { Scene } from './scene';
import { Serializer } from './serializer';
import { Transform } from './transform';

// definition of a procedural content object (persisted in the document)
type SceneObjectDef =
    | { kind: 'image'; src: string; aspect: number; billboard: boolean }
    | { kind: 'text'; text: string; color: string; bgColor: string; fontSize: number; billboard: boolean }
    | { kind: 'shape'; shape: 'box' | 'sphere' | 'plane'; color: [number, number, number] };

const vec = new Vec3();
const invWorld = new Mat4();
const camPos = new Vec3();

let counter = 0;

// build a quad mesh in the XY plane facing +Z, with the given half extents
const buildQuad = (device: any, hw: number, hh: number): Mesh => {
    const mesh = new Mesh(device);
    mesh.setPositions([-hw, -hh, 0, hw, -hh, 0, hw, hh, 0, -hw, hh, 0]);
    mesh.setNormals([0, 0, 1, 0, 0, 1, 0, 0, 1, 0, 0, 1]);
    mesh.setUvs(0, [0, 1, 1, 1, 1, 0, 0, 0]);
    mesh.setIndices([0, 1, 2, 0, 2, 3]);
    mesh.update();
    return mesh;
};

// an unlit textured material (shows its texture regardless of scene lighting)
const buildUnlitTextured = (texture: Texture, transparent: boolean): StandardMaterial => {
    const material = new StandardMaterial();
    material.diffuse = new Color(0, 0, 0);
    material.emissive = new Color(1, 1, 1);
    material.emissiveMap = texture;
    material.useLighting = false;
    material.cull = CULLFACE_NONE;
    if (transparent) {
        material.opacityMap = texture;
        material.opacityMapChannel = 'a';
        material.blendType = BLEND_NORMAL;
    }
    material.update();
    return material;
};

// A procedural content object (image plane, text label, or shape primitive).
// Uses ElementType.model so it inherits selection, multi-select, transform gizmo
// and scene-list behaviour. Persisted separately from imported glB models.
class SceneObject extends Element {
    entity: Entity;
    def: SceneObjectDef;

    _name = '';
    _visible = true;

    localBoundStorage = new BoundingBox();
    worldBoundStorage = new BoundingBox();

    constructor(def: SceneObjectDef, name: string) {
        super(ElementType.model);
        this.def = def;
        this._name = name;
        this.entity = new Entity(`sceneObject_${counter++}`);
    }

    // (re)build the renderable entity from the definition + a ready resource
    build(device: any, texture?: Texture, aspect = 1) {
        const def = this.def;
        let mesh: Mesh;
        let material: StandardMaterial;

        if (def.kind === 'shape') {
            material = new StandardMaterial();
            material.diffuse = new Color(def.color[0], def.color[1], def.color[2]);
            material.update();
            if (def.shape === 'box') {
                mesh = Mesh.fromGeometry(device, new BoxGeometry());
            } else if (def.shape === 'sphere') {
                mesh = Mesh.fromGeometry(device, new SphereGeometry());
            } else {
                mesh = buildQuad(device, 0.5, 0.5);
            }
        } else {
            // image or text: textured quad sized to its aspect ratio
            const hh = 0.5;
            const hw = 0.5 * aspect;
            mesh = buildQuad(device, hw, hh);
            material = buildUnlitTextured(texture, true);
        }

        const meshInstance = new MeshInstance(mesh, material);
        this.entity.addComponent('render', { meshInstances: [meshInstance] });
    }

    get meshInstances(): MeshInstance[] {
        const result: MeshInstance[] = [];
        this.entity.findComponents('render').forEach((r: any) => result.push(...r.meshInstances));
        return result;
    }

    destroy() {
        super.destroy();
        this.entity.destroy();
    }

    async add() {
        this.scene.attachEntity(this);
        this.updateWorldBound();
    }

    remove() {
        this.scene.detachEntity(this);
        this.scene.boundDirty = true;
    }

    serialize(serializer: Serializer) {
        serializer.packa(this.entity.getWorldTransform().data);
        serializer.pack(this.visible);
    }

    onPreRender() {
        // billboard image/text objects always face the camera
        const def = this.def;
        if ((def.kind === 'image' || def.kind === 'text') && def.billboard) {
            camPos.copy(this.scene.camera.mainCamera.getPosition());
            this.entity.lookAt(camPos);
            this.entity.rotateLocal(0, 180, 0);
        }
        // ancestor groups can hide this element without changing its own `visible` flag
        this.updateEffectiveVisibility();
    }

    focalPoint() {
        return this.worldBound.center;
    }

    move(position?: Vec3, rotation?: Quat, scale?: Vec3) {
        const { entity } = this;
        if (position) entity.setLocalPosition(position);
        if (rotation) entity.setLocalRotation(rotation);
        if (scale) entity.setLocalScale(scale);
        this.updateWorldBound();
        this.scene.events.fire('model.moved', this);
    }

    private updateWorldBound() {
        const instances = this.meshInstances;
        if (instances.length === 0) {
            this.scene.boundDirty = true;
            return;
        }
        this.worldBoundStorage.copy(instances[0].aabb);
        for (let i = 1; i < instances.length; ++i) {
            this.worldBoundStorage.add(instances[i].aabb);
        }
        invWorld.copy(this.entity.getWorldTransform()).invert();
        this.localBoundStorage.setFromTransformedAabb(this.worldBoundStorage, invWorld);
        this.scene.boundDirty = true;
    }

    get localBound() {
        return this.localBoundStorage;
    }

    get worldBound() {
        return this.worldBoundStorage;
    }

    get worldTransform() {
        return this.entity.getWorldTransform();
    }

    set name(value: string) {
        if (value !== this._name) {
            this._name = value;
            this.scene?.events.fire('model.name', this);
        }
    }

    get name() {
        return this._name;
    }

    set visible(value: boolean) {
        if (value !== this._visible) {
            this._visible = value;
            this.updateEffectiveVisibility();
            this.scene?.events.fire('model.visibility', this);
        }
    }

    get visible() {
        return this._visible;
    }

    // Rendering depends on this element AND every ancestor group being visible, so hiding
    // a group hides its contents without destroying each child's own visibility state.
    // Guarded for the window before scene.add(), when there is no scene to ask.
    updateEffectiveVisibility() {
        this.entity.enabled = this.scene ? this.scene.effectiveVisibility(this) : this._visible;
    }

    getPivot(mode: 'center' | 'boundCenter', selection: boolean, result: Transform) {
        const { entity } = this;
        switch (mode) {
            case 'center':
                result.set(entity.getLocalPosition(), entity.getLocalRotation(), entity.getLocalScale());
                break;
            case 'boundCenter':
                entity.getLocalTransform().transformPoint(this.localBound.center, vec);
                result.set(vec, entity.getLocalRotation(), entity.getLocalScale());
                break;
        }
    }

    docSerialize() {
        const pack3 = (v: Vec3) => [v.x, v.y, v.z];
        const pack4 = (q: Quat) => [q.x, q.y, q.z, q.w];
        return {
            object: this.def,
            name: this.name,
            position: pack3(this.entity.getLocalPosition()),
            rotation: pack4(this.entity.getLocalRotation()),
            scale: pack3(this.entity.getLocalScale()),
            visible: this.visible
        };
    }

    docDeserialize(doc: any) {
        const { name, position, rotation, scale, visible } = doc;
        this.name = name;
        this.move(new Vec3(position), new Quat(rotation), new Vec3(scale));
        this.visible = visible;
    }
}

// render text to a canvas and return it plus its aspect ratio
const renderTextCanvas = (def: { text: string; color: string; bgColor: string; fontSize: number }) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const fontSize = def.fontSize || 64;
    const font = `bold ${fontSize}px Arial, Helvetica, sans-serif`;
    ctx.font = font;
    const pad = fontSize * 0.5;
    const textW = ctx.measureText(def.text || ' ').width;
    canvas.width = Math.ceil(textW + pad * 2);
    canvas.height = Math.ceil(fontSize * 1.4 + pad);

    // canvas resize resets the context state, so re-apply
    ctx.font = font;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';

    if (def.bgColor && def.bgColor !== 'transparent') {
        const r = Math.min(canvas.height * 0.25, 24);
        ctx.fillStyle = def.bgColor;
        ctx.beginPath();
        ctx.moveTo(r, 0);
        ctx.arcTo(canvas.width, 0, canvas.width, canvas.height, r);
        ctx.arcTo(canvas.width, canvas.height, 0, canvas.height, r);
        ctx.arcTo(0, canvas.height, 0, 0, r);
        ctx.arcTo(0, 0, canvas.width, 0, r);
        ctx.fill();
    }

    ctx.fillStyle = def.color || '#ffffff';
    ctx.fillText(def.text || '', canvas.width / 2, canvas.height / 2);

    return { canvas, aspect: canvas.width / canvas.height };
};

const textureFromImageLike = (device: any, source: HTMLImageElement | HTMLCanvasElement): Texture => {
    const tex = new Texture(device, {
        name: 'sceneObjectTexture',
        mipmaps: true,
        minFilter: FILTER_LINEAR_MIPMAP_LINEAR,
        magFilter: FILTER_LINEAR,
        addressU: ADDRESS_CLAMP_TO_EDGE,
        addressV: ADDRESS_CLAMP_TO_EDGE
    });
    tex.setSource(source);
    return tex;
};

// factory: create and build a scene object from a definition
const createSceneObject = (scene: Scene, def: SceneObjectDef, name: string): Promise<SceneObject> => {
    const device = scene.graphicsDevice;
    const obj = new SceneObject(def, name);

    if (def.kind === 'shape') {
        obj.build(device);
        return Promise.resolve(obj);
    }

    if (def.kind === 'text') {
        const { canvas, aspect } = renderTextCanvas(def);
        obj.build(device, textureFromImageLike(device, canvas), aspect);
        return Promise.resolve(obj);
    }

    // image: load async
    return new Promise((resolve) => {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => {
            const aspect = img.width / img.height;
            (obj.def as any).aspect = aspect;
            obj.build(device, textureFromImageLike(device, img), aspect);
            resolve(obj);
        };
        img.onerror = () => {
            // fall back to a unit quad so the object still exists
            obj.build(device, textureFromImageLike(device, document.createElement('canvas')), 1);
            resolve(obj);
        };
        img.src = def.src;
    });
};

export { SceneObject, SceneObjectDef, createSceneObject };
